import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import * as Lucide from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { MODULES } from "@/data/course";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { CheckCircle2, ArrowLeft, ArrowRight, GraduationCap, ExternalLink } from "lucide-react";

const INTERACTIVE_LINKS = {
  chart: { to: "/charts", label: "Open Interactive Charts" },
  candles: { to: "/charts", label: "Explore Candles on Charts" },
  exercises: { to: "/exercises", label: "Try Trend Exercises" },
  indicators: { to: "/charts", label: "Toggle Indicators on Charts" },
  calculator: { to: "/calculator", label: "Open Risk Calculator" },
  plan: { to: "/course/13", label: "Build your Trading Plan below" },
  backtest: { to: "/simulator", label: "Practice in the Simulator" },
  simulator: { to: "/simulator", label: "Open the $100k Simulator" },
};

function TradingPlanBuilder() {
  const [form, setForm] = useState({ name: "My Trading Plan", market: "", timeframe: "", entry_conditions: "", stop_loss: "", take_profit: "", max_risk: "", exit_conditions: "", avoid_conditions: "" });
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => { try { const { data } = await api.get("/plans"); setPlans(data); } catch {} };
  React.useEffect(() => { load(); }, []);

  const fields = [
    ["market", "Market", "e.g. Crypto — BTC/USD"],
    ["timeframe", "Timeframe", "e.g. 1h / 4h"],
    ["entry_conditions", "Conditions to enter", "e.g. Uptrend + bounce off support"],
    ["stop_loss", "Stop Loss rule", "e.g. Below the last swing low"],
    ["take_profit", "Take Profit rule", "e.g. Previous resistance / 1:2 R:R"],
    ["max_risk", "Max risk per trade", "e.g. 1% of capital"],
    ["exit_conditions", "Conditions to exit", "e.g. Trend structure breaks"],
    ["avoid_conditions", "When NOT to trade", "e.g. Major news, unclear setup"],
  ];

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/plans", form);
      toast.success("Trading plan saved to your profile");
      load();
    } catch { toast.error("Could not save plan"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { await api.delete(`/plans/${id}`); load(); };

  return (
    <div className="mt-8 bg-[#0F172A] border border-slate-800 rounded-2xl p-6" data-testid="plan-builder">
      <h3 className="font-display text-xl font-semibold mb-4">Build your Trading Plan</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2 space-y-1.5">
          <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Plan name</label>
          <input data-testid="plan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
        </div>
        {fields.map(([key, label, ph]) => (
          <div key={key} className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">{label}</label>
            <input data-testid={`plan-${key}`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={ph}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none placeholder:text-slate-600" />
          </div>
        ))}
      </div>
      <button data-testid="plan-save" onClick={save} disabled={saving} className="mt-5 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors disabled:opacity-60">
        {saving ? "Saving…" : "Save Plan"}
      </button>

      {plans.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-semibold text-slate-300">Your saved plans</h4>
          {plans.map((p) => (
            <div key={p.id} data-testid={`saved-plan-${p.id}`} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-100">{p.name}</span>
                <button onClick={() => remove(p.id)} className="text-xs text-red-400 hover:text-red-300">Delete</button>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-slate-400 text-xs">
                {p.market && <span>Market: <span className="text-slate-300">{p.market}</span></span>}
                {p.timeframe && <span>Timeframe: <span className="text-slate-300">{p.timeframe}</span></span>}
                {p.max_risk && <span>Max risk: <span className="text-slate-300">{p.max_risk}</span></span>}
                {p.entry_conditions && <span>Entry: <span className="text-slate-300">{p.entry_conditions}</span></span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ModuleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const mid = parseInt(id, 10);
  const module = MODULES.find((m) => m.id === mid);
  const { progress, setProgress } = useAuth();
  const { user } = useAuth();
  const [marking, setMarking] = useState(false);

  if (!module) return <div className="text-slate-400">Module not found. <Link to="/course" className="text-blue-400">Back to course</Link></div>;

  const done = progress?.completed_modules?.includes(mid);
  const Icon = Lucide[module.icon] || Lucide.BookOpen;
  const inter = module.interactive ? INTERACTIVE_LINKS[module.interactive] : null;
  const access = user?.plan === "pro" || (user?.free_modules || []).includes(mid);

  const markComplete = async () => {
    setMarking(true);
    try {
      const { data } = await api.post("/progress/complete-module", { module_id: mid });
      setProgress(data);
      toast.success("Module completed! +50 XP");
    } catch { toast.error("Could not update progress"); }
    finally { setMarking(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link to="/course" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200" data-testid="back-to-course">
        <ArrowLeft className="w-4 h-4" /> Course
      </Link>

      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-2xl bg-blue-500/15 border border-blue-500/20 grid place-items-center shrink-0">
          <Icon className="w-7 h-7 text-blue-300" />
        </div>
        <div>
          <div className="text-xs font-mono text-slate-500">Module {module.id} · {module.stage} · {module.minutes} min</div>
          <h1 className="font-display text-3xl font-black tracking-tight">{module.title}</h1>
        </div>
      </div>

      {mid === 11 && <DisclaimerBanner />}

      {!access ? (
        <div className="space-y-6">
          <UpgradePrompt title={`Module ${mid} is a Pro feature`} description="Unlock all 15 course modules with Pro (€3.99/month). Quizzes stay free for everyone." />
          <div className="text-center">
            <Link to={`/quizzes/${mid}`} data-testid="module-quiz-link" className="text-sm font-semibold text-blue-400 hover:text-blue-300">Take the free quiz for this module →</Link>
          </div>
        </div>
      ) : (
      <>
      <article className="space-y-6">
        {module.sections.map((s, i) => (
          <section key={i} className="bg-[#0F172A] border border-slate-800 rounded-2xl p-6">
            <h2 className="font-display text-xl font-semibold mb-3 text-blue-200">{s.h}</h2>
            {s.p && s.p.map((para, j) => <p key={j} className="text-slate-300 leading-relaxed mb-2 last:mb-0">{para}</p>)}
            {s.list && (
              <ul className="space-y-2 mt-2">
                {s.list.map((li, j) => (
                  <li key={j} className="flex gap-2.5 text-slate-300 leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 shrink-0" />{li}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </article>

      {inter && module.interactive !== "plan" && (
        <Link to={inter.to} data-testid="module-interactive-link" className="flex items-center justify-between bg-blue-500/10 border border-blue-500/25 rounded-xl px-5 py-4 hover:bg-blue-500/15 transition-colors">
          <span className="font-medium text-blue-200">{inter.label}</span>
          <ExternalLink className="w-4 h-4 text-blue-300" />
        </Link>
      )}

      {module.interactive === "plan" && <TradingPlanBuilder />}

      <div className="flex flex-wrap items-center gap-3 pt-2">
        {done ? (
          <span className="inline-flex items-center gap-2 text-emerald-400 font-semibold" data-testid="module-completed-badge"><CheckCircle2 className="w-5 h-5" /> Completed</span>
        ) : (
          <button data-testid="mark-complete-btn" onClick={markComplete} disabled={marking} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl px-5 py-2.5 transition-colors disabled:opacity-60">
            <CheckCircle2 className="w-4 h-4" /> {marking ? "Saving…" : "Mark as complete (+50 XP)"}
          </button>
        )}
        <Link to={`/quizzes/${mid}`} data-testid="module-quiz-link" className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 font-semibold rounded-xl px-5 py-2.5 transition-colors">
          <GraduationCap className="w-4 h-4" /> Take the Quiz
        </Link>
        {mid < 15 && (
          <button onClick={() => navigate(`/course/${mid + 1}`)} data-testid="next-module-btn" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 font-medium ml-auto">
            Next module <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
      </>
      )}
    </div>
  );
}
